import { useCallback, useEffect, useState } from 'react'

// Generic message-search state, paired with a useChatTranscript. Feed it an
// `api` adapter:
//
//   api.search({ q, limit }) -> Promise<{ results }>
//       result: { _id, role, createdAt, snippet }
//
// It owns the open/closed panel, the query, and the fetched results. Selecting a
// result is handled by the caller (typically transcript.jumpTo).
export default function useMessageSearch(api) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [ran, setRan] = useState(false)

  // Reset when the source changes (e.g. switching rooms).
  useEffect(() => {
    setOpen(false)
    setQuery('')
    setResults([])
    setError('')
    setRan(false)
  }, [api])

  const run = useCallback(async () => {
    if (!api) return
    const q = query.trim()
    if (!q) {
      setResults([])
      setRan(false)
      return
    }
    setLoading(true)
    setError('')
    try {
      const data = await api.search({ q })
      setResults(data.results || [])
      setRan(true)
    } catch (err) {
      setError(err.message || 'Search failed')
      setResults([])
      setRan(true)
    } finally {
      setLoading(false)
    }
  }, [api, query])

  const openSearch = useCallback(() => setOpen(true), [])

  const close = useCallback(() => {
    setOpen(false)
    setQuery('')
    setResults([])
    setError('')
    setRan(false)
  }, [])

  return { open, query, setQuery, results, loading, error, ran, run, openSearch, close }
}
